import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getResource, requestFreeDownload, createCheckout } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import BrandBand from "@/components/BrandBand";

export default function ResourceDetail() {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    setResource(null);
    setDownloaded(false);
    getResource(slug).then(setResource).catch(() => setResource({ notFound: true }));
    window.scrollTo(0, 0);
  }, [slug]);

  if (!resource) return <div className="section max-w-[900px] mx-auto px-6 opacity-60">Loading…</div>;
  if (resource.notFound) return <div className="section max-w-[900px] mx-auto px-6 font-serif-editorial text-3xl">Resource not found.</div>;

  const handleFree = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await requestFreeDownload(slug, email);
      setDownloaded(true);
      analytics.freeDownload(slug);
      toast.success("Sent. Check your inbox — the download link is on its way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckout(slug, origin, email || null);
      analytics.checkoutInitiated(slug, resource.price);
      window.location.href = res.url;
    } catch (err) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="resource-detail-page">
      <section className="section pb-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-16 items-start">
          <div className="md:col-span-6">
            <img src={resource.image} alt={resource.title} className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="overline">{resource.premium ? `Premium · $${resource.price.toFixed(2)}` : "Free download"}</div>
            <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1 }} data-testid="resource-title">
              {resource.title}
            </h1>
            <p className="mt-8 text-lg" style={{ lineHeight: 1.6 }}>{resource.description}</p>

            <div className="mt-10 flex flex-wrap gap-2">
              {resource.tags?.map((t) => (
                <span key={t} className="overline px-3 py-2 border" style={{ borderColor: "rgba(79,87,70,0.4)" }}>{t}</span>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="overline">Pages</div>
                <div className="mt-2 font-serif-editorial text-xl">{resource.pages}</div>
              </div>
              <div>
                <div className="overline">Level</div>
                <div className="mt-2 font-serif-editorial text-xl capitalize">{resource.level}</div>
              </div>
            </div>

            <div className="rule mt-12" />

            {!resource.premium && !downloaded && (
              <form onSubmit={handleFree} className="mt-10 space-y-4" data-testid="free-download-form">
                <div className="overline">Enter your email to receive the guide</div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="free-download-email"
                  className="w-full px-4 py-3 border text-sm outline-none"
                  style={{ borderColor: "rgba(79,87,70,0.4)", backgroundColor: "transparent" }}
                />
                <button type="submit" disabled={loading} className="btn-olive" data-testid="free-download-submit">
                  {loading ? "Sending…" : "Get the free guide"}
                </button>
                <div className="text-xs opacity-70">You will also be subscribed to the SIBO Institute weekly.</div>
              </form>
            )}

            {!resource.premium && downloaded && (
              <div className="mt-10" data-testid="free-download-done">
                <div className="font-serif-editorial text-2xl">On its way.</div>
                <div className="mt-3 text-sm opacity-80">Check your inbox — we've sent the download link to <strong>{email}</strong>. Give it a minute; if it doesn't arrive, check spam.</div>
              </div>
            )}

            {resource.premium && (
              <div className="mt-10 space-y-4" data-testid="premium-purchase">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for receipt and download link"
                  required
                  data-testid="premium-email-input"
                  className="w-full px-4 py-3 border text-sm outline-none"
                  style={{ borderColor: "rgba(79,87,70,0.4)", backgroundColor: "transparent" }}
                />
                <button onClick={handlePurchase} disabled={loading || !email} className="btn-olive" data-testid="premium-purchase-btn">
                  {loading ? "Redirecting…" : `Purchase — $${resource.price.toFixed(2)}`}
                </button>
                <div className="text-xs opacity-70">Secure checkout via Stripe. Your PDF will be emailed to you the moment payment clears.</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/resources" className="editorial-link overline" data-testid="back-to-resources">← All resources</Link>
        </div>
      </section>

      <BrandBand />
    </div>
  );
}
